import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

interface NicknameModalProps {
    onNicknameSet: (nickname: string) => void;
    canDismiss?: boolean;  // Optional: whether modal can be dismissed (default: true)
}

const NicknameModal: React.FC<NicknameModalProps> = ({ onNicknameSet, canDismiss = true }) => {
    const { token } = useAuth();  // Get auth token
    const [nickname, setNickname] = useState('');
    const [nicknameAvailable, setNicknameAvailable] = useState<boolean | null>(null);
    const [nicknameChecking, setNicknameChecking] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const checkNicknameAvailability = async (nick: string) => {
        if (nick.length < 3) {
            setNicknameAvailable(null);
            return;
        }

        setNicknameChecking(true);
        try {
            const response = await axios.post('/api/users/nickname/check/',
                { nickname: nick },
                { headers: { Authorization: `Token ${token}` } }
            );
            setNicknameAvailable(response.data.available);
            if (!response.data.available) {
                setError(response.data.message);
            } else {
                setError('');
            }
        } catch (err) {
            setNicknameAvailable(null);
            setError('Unable to check nickname availability');
        } finally {
            setNicknameChecking(false);
        }
    };

    const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNickname(value);
        setError('');

        // Debounce check
        if (value.length >= 3) {
            setTimeout(() => checkNicknameAvailability(value), 500);
        } else {
            setNicknameAvailable(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!nickname || nickname.length < 3) {
            setError('Nickname must be at least 3 characters');
            return;
        }

        if (nicknameAvailable === false) {
            setError('This nickname is not available');
            return;
        }

        setSubmitting(true);
        try {
            const response = await axios.post('/api/users/nickname/setup/',
                { nickname },
                { headers: { Authorization: `Token ${token}` } }
            );
            onNicknameSet(response.data.nickname);
        } catch (err: any) {
            const errorMessage = err.response?.data?.nickname?.[0] || 'Failed to set nickname. Please try again.';
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={canDismiss ? undefined : (e) => e.stopPropagation()}
        >
            <div className="w-full max-w-md p-8 space-y-6 bg-secondary rounded-xl border border-accent/20 mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-accent mb-2">Choose Your Display Nickname</h2>
                    <p className="text-sm text-gray-400">
                        {canDismiss
                            ? "For your privacy, you need a public display name (nickname) before continuing."
                            : "⚠️ Required: You must set a nickname to continue using Wisdom Book. This protects your privacy in all public interactions."
                        }
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-2">Display Nickname</label>
                        <input
                            type="text"
                            value={nickname}
                            onChange={handleNicknameChange}
                            className={`w-full px-4 py-3 bg-primary border rounded-lg focus:ring-2 focus:ring-accent focus:outline-none text-black ${nicknameAvailable === true ? 'border-green-500' :
                                nicknameAvailable === false ? 'border-red-500' :
                                    'border-gray-700'
                                }`}
                            placeholder="e.g., wisdom_seeker"
                            required
                            minLength={3}
                            maxLength={50}
                            autoFocus
                        />
                        {nicknameChecking && (
                            <p className="text-xs text-gray-400 mt-2">Checking availability...</p>
                        )}
                        {nicknameAvailable === true && nickname.length >= 3 && (
                            <p className="text-xs text-green-500 mt-2">✓ Available! This nickname is yours.</p>
                        )}
                        {nicknameAvailable === false && nickname.length >= 3 && (
                            <p className="text-xs text-red-500 mt-2">{error || 'This nickname is already taken'}</p>
                        )}
                        {nickname.length > 0 && nickname.length < 3 && (
                            <p className="text-xs text-gray-400 mt-2">Minimum 3 characters</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                            Letters, numbers, underscores, and hyphens only
                        </p>
                    </div>

                    {error && !nicknameChecking && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-sm text-red-400">{error}</p>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting || nicknameChecking || nicknameAvailable !== true}
                        className="w-full py-3 font-bold text-primary bg-accent rounded-lg hover:bg-accent/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Setting Nickname...' : 'Continue'}
                    </button>
                </form>

                <div className="p-4 bg-accent/10 border border-accent/20 rounded-lg">
                    <p className="text-xs text-gray-300">
                        <strong>Why a nickname?</strong> Your nickname protects your privacy by ensuring
                        your real name and email remain private in all public interactions.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NicknameModal;
